import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Modal,
  TextField,
  Select,
  Banner,
  DataTable,
  Box,
  Divider,
  ButtonGroup,
} from '@shopify/polaris';
import { estimatesApi, directRentalApi } from '@/services';
import type { EstimateStatus, ConvertInput } from '@/services/estimates';

const STATUS_TONE: Record<EstimateStatus, 'success' | 'attention' | 'info' | 'warning' | 'critical' | undefined> = {
  draft: undefined, sent: 'info', accepted: 'success', converted: 'success', expired: 'warning',
};

function fmtMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function EstimateDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['estimates.get', id],
    queryFn: () => estimatesApi.get(id),
    enabled: !!id,
  });

  const wrapsQuery = useQuery({
    queryKey: ['rent.wraps'],
    queryFn: () => directRentalApi.getWraps(),
    enabled: !!query.data && query.data.type === 'rental' && query.data.status !== 'converted',
  });

  const [convertOpen, setConvertOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conversionResult, setConversionResult] = useState<{ invoiceUrl?: string } | null>(null);

  // Convert dialog state
  const [surgeryDate, setSurgeryDate] = useState('');
  const [wrapProductId, setWrapProductId] = useState('');
  const [wrapVariantId, setWrapVariantId] = useState('');

  const sendMutation = useMutation({
    mutationFn: () => estimatesApi.markSent(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['estimates.get', id] }),
    onError: (e: any) => setActionError(e?.response?.data?.message || e?.message || 'Failed to mark sent'),
  });

  const acceptMutation = useMutation({
    mutationFn: () => estimatesApi.markAccepted(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['estimates.get', id] }),
    onError: (e: any) => setActionError(e?.response?.data?.message || e?.message || 'Failed to mark accepted'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => estimatesApi.delete(id),
    onSuccess: () => navigate('/estimates'),
    onError: (e: any) => setActionError(e?.response?.data?.message || e?.message || 'Failed to delete'),
  });

  const convertMutation = useMutation({
    mutationFn: (input: ConvertInput) => estimatesApi.convert(id, input),
    onSuccess: (result) => {
      setConvertOpen(false);
      setConversionResult({ invoiceUrl: result.invoiceUrl });
      queryClient.invalidateQueries({ queryKey: ['estimates.get', id] });
    },
    onError: (e: any) => setActionError(e?.response?.data?.message || e?.message || 'Conversion failed'),
  });

  if (query.isLoading) {
    return <Page title="Estimate"><Card><Text as="p">Loading…</Text></Card></Page>;
  }
  if (query.isError || !query.data) {
    return <Page title="Estimate"><Banner tone="critical">Estimate not found.</Banner></Page>;
  }

  const e = query.data;
  const editable = e.status === 'draft' || e.status === 'sent';
  const convertible = e.status === 'draft' || e.status === 'sent' || e.status === 'accepted';

  const lineRows = e.lines.map((line) => [
    line.activity,
    <Box key={`${line.id}-desc`}>
      <Text as="p" fontWeight="medium">{line.productName}</Text>
      {line.description && <Text as="p" tone="subdued" variant="bodySm">{line.description}</Text>}
    </Box>,
    String(line.quantity),
    fmtMoney(line.rateCents),
    fmtMoney(line.amountCents),
  ]);

  const wraps = wrapsQuery.data ?? [];
  const wrapProductOptions = [
    { label: 'Select a wrap…', value: '' },
    ...wraps.map((w: any) => ({ label: w.title, value: w.id })),
  ];
  const selectedWrap = wraps.find((w: any) => w.id === wrapProductId);
  const wrapVariantOptions = selectedWrap ? selectedWrap.variants.map((v: any) => ({ label: v.title, value: v.id })) : [];

  function handleConvertSubmit() {
    setActionError(null);
    if (e.type === 'rental') {
      if (!surgeryDate || !wrapProductId || !wrapVariantId) {
        setActionError('Surgery date, wrap product, and wrap variant are required.');
        return;
      }
      convertMutation.mutate({
        surgeryDate,
        wrapProductId,
        wrapVariantId,
        addOns: [],
      });
    } else {
      convertMutation.mutate({});
    }
  }

  return (
    <Page
      title={`Estimate ${e.estimateNumber}`}
      subtitle={`${e.type === 'sale' ? 'Sale' : 'Rental'} estimate`}
      backAction={{ content: 'Estimates', onAction: () => navigate('/estimates') }}
    >
      <BlockStack gap="400">
        {actionError && <Banner tone="critical" onDismiss={() => setActionError(null)}>{actionError}</Banner>}

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h2" variant="headingLg">{e.estimateNumber}</Text>
                <Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge>
              </InlineStack>
              <ButtonGroup>
                <Button onClick={() => window.open(estimatesApi.pdfUrl(e.id), '_blank')}>Print PDF</Button>
                {e.status === 'draft' && (
                  <Button onClick={() => sendMutation.mutate()} loading={sendMutation.isPending}>Mark Sent</Button>
                )}
                {e.status === 'sent' && (
                  <Button onClick={() => acceptMutation.mutate()} loading={acceptMutation.isPending}>Mark Accepted</Button>
                )}
                {convertible && (
                  <Button variant="primary" onClick={() => { setConvertOpen(true); setActionError(null); }}>Convert to Order</Button>
                )}
                {editable && (
                  <Button tone="critical" onClick={() => { if (confirm('Delete this estimate?')) deleteMutation.mutate(); }} loading={deleteMutation.isPending}>Delete</Button>
                )}
              </ButtonGroup>
            </InlineStack>
          </BlockStack>
        </Card>

        {(conversionResult?.invoiceUrl || e.convertedToInvoiceUrl) && (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Payment link</Text>
              <Text as="p">Send this checkout link to the patient:</Text>
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <Text as="p" fontWeight="medium">{conversionResult?.invoiceUrl || e.convertedToInvoiceUrl}</Text>
              </Box>
              <InlineStack gap="200">
                <Button onClick={() => navigator.clipboard.writeText(conversionResult?.invoiceUrl || e.convertedToInvoiceUrl || '')}>Copy</Button>
                <Button onClick={() => window.open(conversionResult?.invoiceUrl || e.convertedToInvoiceUrl || '', '_blank')}>Open</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        )}

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Patient & shipping</Text>
            <InlineStack gap="800" wrap>
              <BlockStack gap="100">
                <Text as="p" tone="subdued" variant="bodySm">PATIENT</Text>
                <Text as="p" fontWeight="medium">{`${e.patientFirstName} ${e.patientLastName}`}</Text>
                {e.patientEmail && <Text as="p" variant="bodySm">{e.patientEmail}</Text>}
                {e.patientPhone && <Text as="p" variant="bodySm">{e.patientPhone}</Text>}
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued" variant="bodySm">SHIP TO</Text>
                <Text as="p">{e.shipAddress.street}</Text>
                <Text as="p">{`${e.shipAddress.city}, ${e.shipAddress.province} ${e.shipAddress.postalCode}`}</Text>
                {e.shipVia && <Text as="p" variant="bodySm">via {e.shipVia}</Text>}
              </BlockStack>
              <BlockStack gap="100">
                <Text as="p" tone="subdued" variant="bodySm">DATES</Text>
                <Text as="p">Estimate: {new Date(e.estimateDate).toLocaleDateString()}</Text>
                <Text as="p">Expires: {new Date(e.expirationDate).toLocaleDateString()}</Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Line items</Text>
            <DataTable
              columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
              headings={['Activity', 'Description', 'Qty', 'Rate', 'Amount']}
              rows={lineRows}
            />
            <Divider />
            <BlockStack gap="100" align="end">
              <InlineStack gap="800" align="end">
                <Text as="p" tone="subdued">Subtotal</Text>
                <Text as="p">{fmtMoney(e.subtotalCents)}</Text>
              </InlineStack>
              <InlineStack gap="800" align="end">
                <Text as="p" tone="subdued">{`Tax (${(e.taxRateBps / 100).toFixed(3).replace(/\.?0+$/, '')}%)`}</Text>
                <Text as="p">{fmtMoney(e.taxAmountCents)}</Text>
              </InlineStack>
              <InlineStack gap="800" align="end">
                <Text as="p" variant="headingMd">Total CAD</Text>
                <Text as="p" variant="headingMd">{fmtMoney(e.totalCents)}</Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        {e.notes && (
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">Notes</Text>
              <Text as="p">{e.notes}</Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>

      <Modal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        title={`Convert ${e.type === 'rental' ? 'rental' : 'sale'} estimate to order`}
        primaryAction={{
          content: 'Confirm',
          onAction: handleConvertSubmit,
          loading: convertMutation.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConvertOpen(false) }]}
      >
        <Modal.Section>
          {e.type === 'rental' ? (
            <BlockStack gap="300">
              <Text as="p">A draft order will be built for this rental. Provide the missing details:</Text>
              <TextField
                label="Surgery date"
                type="date"
                value={surgeryDate}
                onChange={setSurgeryDate}
                autoComplete="off"
                requiredIndicator
              />
              <Select
                label="Wrap product"
                options={wrapProductOptions}
                value={wrapProductId}
                onChange={(v) => { setWrapProductId(v); setWrapVariantId(''); }}
                requiredIndicator
              />
              {selectedWrap && (
                <Select
                  label="Wrap variant"
                  options={wrapVariantOptions}
                  value={wrapVariantId}
                  onChange={setWrapVariantId}
                  requiredIndicator
                />
              )}
            </BlockStack>
          ) : (
            <BlockStack gap="300">
              <Text as="p">A draft order will be built with the line items below at their estimate-time prices.</Text>
              <Text as="p" tone="subdued" variant="bodySm">Customer pays via Shopify Checkout. The estimate will be marked as converted.</Text>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
